import React, { useMemo, useState, useCallback } from 'react';
import { Select, Modal, message } from 'antd';
import { updateDataCollection } from '../../services/api';

/**
 * Inline owner picker for admins: searchable user list, clear = platform/system.
 * Confirms before calling the API.
 */
const CollectionOwnerSelect = ({
    ownerId,
    options,
    loading = false,
    collectionId,
    collectionName,
    keycloak,
    onUpdated,
    className,
    style,
    minWidth = 0,
    getPopupContainer,
    /** `borderless` for table cells; `outlined` for settings forms on tinted panels */
    selectVariant = 'borderless',
}) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pending, setPending] = useState({ value: '', label: '' });

    const mergedOptions = useMemo(() => {
        const oid = String(ownerId ?? '').trim();
        if (!oid || options.some((o) => String(o.value) === oid)) return options;
        return [
            { value: oid, label: `User ${oid.slice(0, 8)}…` },
            ...options,
        ];
    }, [ownerId, options]);

    const normalizedOwner = String(ownerId ?? '').trim();

    const handleChange = (newVal) => {
        const next = String(newVal ?? '').trim();
        if (next === normalizedOwner) return;

        const nextLabel = next
            ? (mergedOptions.find((o) => String(o.value) === next)?.label || 'Selected user')
            : 'Platform (no individual owner)';

        setPending({ value: next, label: nextLabel });
        setConfirmOpen(true);
    };

    const closeConfirm = useCallback(() => {
        setConfirmOpen(false);
    }, []);

    const submitTransfer = useCallback(async () => {
        try {
            const fd = new FormData();
            fd.append('owner_id', pending.value);
            await updateDataCollection(keycloak, collectionId, fd);
            message.success('Owner updated');
            onUpdated?.();
            setConfirmOpen(false);
        } catch (e) {
            message.error(e?.message || 'Failed to update owner');
            throw e;
        }
    }, [pending.value, keycloak, collectionId, onUpdated]);

    return (
        <>
            <Select
                className={className}
                style={{ minWidth: minWidth || undefined, ...style }}
                size={selectVariant === 'borderless' ? 'small' : 'middle'}
                variant={selectVariant}
                allowClear
                showSearch
                loading={loading}
                placeholder="Platform"
                value={normalizedOwner || undefined}
                options={mergedOptions}
                optionFilterProp="label"
                onChange={handleChange}
                getPopupContainer={getPopupContainer}
                popupMatchSelectWidth={false}
            />
            <Modal
                title="Transfer ownership?"
                open={confirmOpen}
                okText="Confirm"
                okType="primary"
                cancelText="Cancel"
                centered
                zIndex={1100}
                destroyOnClose
                onOk={submitTransfer}
                onCancel={closeConfirm}
            >
                {collectionName ? (
                    <p style={{ margin: 0 }}>
                        The collection &ldquo;{collectionName}&rdquo; will be assigned to{' '}
                        <strong>{pending.label}</strong>. This updates who sees it in their personal list.
                    </p>
                ) : (
                    <p style={{ margin: 0 }}>
                        This collection will be assigned to <strong>{pending.label}</strong>.
                    </p>
                )}
            </Modal>
        </>
    );
};

export default CollectionOwnerSelect;
